import { create } from 'zustand';
import {
  generateRoomCode,
  validateRoomCode,
  type Countdown,
  type MediaFingerprint,
  type Participant,
  type RoomSource,
} from '@cinesync/protocol';
import type { PlaybackState } from '@cinesync/sync-core';
import type { MediaEngine } from '@/modules/media/engine';
import {
  RoomConnection,
  type ConnectionStatus,
  type LinkQuality,
} from './roomConnection';
import { SyncController, TICK_INTERVAL_MS, type SyncStats } from './syncController';

/**
 * Room session state.
 *
 * Holds the connection, the sync controller and the tick timer. Components
 * read from here and call the intent methods; none of them touch a socket or a
 * clock directly.
 */

export interface ChatEntry {
  readonly id: string;
  readonly authorId: string;
  readonly body: string;
  readonly atPositionMs: number | null;
  readonly serverTimeMs: number;
  /** True until the relay confirms it, so an optimistic render can be faded. */
  readonly pending: boolean;
}

interface RoomStore {
  readonly code: string | null;
  readonly participantId: string;
  readonly status: ConnectionStatus;
  readonly participants: readonly Participant[];
  readonly playback: PlaybackState | null;
  readonly quality: LinkQuality | null;
  readonly sync: SyncStats | null;
  readonly chat: readonly ChatEntry[];
  readonly typing: readonly string[];
  readonly mediaMismatch: boolean;
  readonly error: string | null;
  /**
   * What the room is watching.
   *
   * The point of holding this centrally is that only one of the two people
   * needs to find the film. The other's copy of the app opens it from here.
   */
  readonly source: RoomSource | null;
  /** Who last announced the source, so the interface can say whose choice it was. */
  readonly sourceActorId: string | null;
  readonly countdown: Countdown | null;

  createRoom: (displayName: string) => string;
  join: (code: string, displayName: string, engine: MediaEngine | null) => boolean;
  attachEngine: (engine: MediaEngine) => void;
  leave: () => void;

  /** Convert a local `performance.now()` reading to the relay's clock. */
  toServerTime: (localMs: number) => number;
  shareSource: (source: RoomSource | null) => void;
  setCountdown: (countdown: Countdown | null) => void;
  announceMedia: (media: MediaFingerprint | null) => void;

  play: () => void;
  pause: (atPositionMs: number) => void;
  seek: (toPositionMs: number) => void;
  setRate: (rate: number) => void;
  sendChat: (body: string, atPositionMs: number | null) => void;
  setTyping: (active: boolean) => void;
}

/**
 * Where the relay lives.
 *
 * Falls back to the self-hosted default so the app works with no configuration
 * at all, which is what makes a LAN session possible without an account.
 */
const RELAY_URL =
  (import.meta.env['VITE_RELAY_URL'] as string | undefined) ?? 'ws://localhost:8787/room';

let connection: RoomConnection | null = null;
let sync: SyncController | null = null;
let tickTimer: ReturnType<typeof setInterval> | null = null;
let attachedEngine: MediaEngine | null = null;

/**
 * The fingerprint sent in `hello`.
 *
 * Held outside the store because it must survive a reconnect: the socket is
 * rebuilt from scratch and would otherwise announce no media at all, silently
 * disabling the mismatch check for the rest of the session.
 */
let announcedMedia: MediaFingerprint | null = null;

function stableParticipantId(): string {
  const key = 'cinesync.participantId';
  try {
    const existing = localStorage.getItem(key);
    if (existing !== null) return existing;
    // Stable across reloads, so a reconnect is recognised as the same person
    // rather than appearing as a second participant.
    const fresh = crypto.randomUUID();
    localStorage.setItem(key, fresh);
    return fresh;
  } catch {
    return crypto.randomUUID();
  }
}

export const useRoomStore = create<RoomStore>((set, get) => ({
  code: null,
  participantId: stableParticipantId(),
  status: 'idle',
  participants: [],
  playback: null,
  quality: null,
  sync: null,
  chat: [],
  typing: [],
  mediaMismatch: false,
  error: null,
  source: null,
  sourceActorId: null,
  countdown: null,

  createRoom: (displayName) => {
    const code = generateRoomCode();
    get().join(code, displayName, attachedEngine);
    return code;
  },

  join: (code, displayName, engine) => {
    const validation = validateRoomCode(code);
    if (!validation.ok) {
      set({ error: validation.message });
      return false;
    }

    get().leave();
    attachedEngine = engine;

    const participantId = get().participantId;

    connection = new RoomConnection({
      relayUrl: RELAY_URL,
      roomCode: validation.code,
      participantId,
      displayName,
      media: announcedMedia,

      onStatus: (status) => set({ status }),
      onQuality: (quality) => set({ quality }),

      onMessage: (message) => {
        switch (message.type) {
          case 'welcome':
            set({
              participants: message.participants,
              playback: message.playback,
              source: message.source,
              countdown: message.countdown,
              // Replayed history, so joining late does not look like an empty
              // room to someone whose brother has been talking for ten minutes.
              chat: message.chat.map((entry) => ({
                id: entry.id,
                authorId: entry.authorId,
                body: entry.body,
                atPositionMs: entry.atPositionMs,
                serverTimeMs: entry.serverTimeMs,
                pending: false,
              })),
              error: null,
            });
            sync?.apply(message.playback);
            break;

          case 'source':
            set({
              source: message.source,
              sourceActorId: message.actorId,
              // A different film means the old mismatch verdict is stale.
              mediaMismatch: false,
            });
            break;

          case 'countdown':
            set({ countdown: message.countdown });
            break;

          case 'playback':
            set({ playback: message.playback });
            sync?.apply(message.playback);
            break;

          case 'participants':
            set({ participants: message.participants });
            break;

          case 'chat':
            set((state) => ({
              // Replace the optimistic copy if this is our own message
              // coming back, rather than showing it twice.
              chat: [
                ...state.chat.filter((entry) => entry.id !== message.localId),
                {
                  id: message.id,
                  authorId: message.authorId,
                  body: message.body,
                  atPositionMs: message.atPositionMs,
                  serverTimeMs: message.serverTimeMs,
                  pending: false,
                },
              ].slice(-300),
            }));
            break;

          case 'typing':
            set((state) => ({
              typing: message.active
                ? [...new Set([...state.typing, message.participantId])]
                : state.typing.filter((id) => id !== message.participantId),
            }));
            break;

          case 'media-mismatch':
            set({ mediaMismatch: true });
            break;

          case 'error':
            set({ error: message.message });
            break;

          case 'pong':
            // Consumed inside the connection; never reaches here.
            break;
        }
      },
    });

    if (engine !== null) {
      sync = new SyncController({
        engine,
        now: () => performance.now(),
        toServerTime: (local) => connection?.toServerTime(local) ?? local,
        onStats: (stats) => set({ sync: stats }),
      });

      tickTimer = setInterval(() => sync?.tick(), TICK_INTERVAL_MS);
    }

    connection.connect();
    set({
      code: validation.code,
      chat: [],
      mediaMismatch: false,
      error: null,
      source: null,
      sourceActorId: null,
      countdown: null,
    });
    return true;
  },

  attachEngine: (engine) => {
    attachedEngine = engine;
    if (connection === null || sync !== null) return;

    // The player mounted after the room was joined, which is the normal order
    // when someone joins first and opens a film afterwards.
    sync = new SyncController({
      engine,
      now: () => performance.now(),
      toServerTime: (local) => connection?.toServerTime(local) ?? local,
      onStats: (stats) => set({ sync: stats }),
    });

    tickTimer = setInterval(() => sync?.tick(), TICK_INTERVAL_MS);

    const playback = get().playback;
    if (playback !== null) sync.apply(playback);
  },

  toServerTime: (localMs) => connection?.toServerTime(localMs) ?? localMs,

  shareSource: (source) => {
    // Set locally as well as sent: the person who chose the film should not
    // wait for a round trip to see their own choice take effect.
    set({ source, sourceActorId: get().participantId, countdown: null });
    connection?.send({ type: 'source', source });
  },

  setCountdown: (countdown) => {
    set({ countdown });
    connection?.send({ type: 'countdown', countdown });
  },

  announceMedia: (media) => {
    announcedMedia = media;
    connection?.send({ type: 'media', media });
  },

  leave: () => {
    connection?.send({ type: 'bye' });
    connection?.disconnect();
    connection = null;

    sync?.disable();
    sync = null;

    if (tickTimer !== null) clearInterval(tickTimer);
    tickTimer = null;

    set({
      code: null,
      status: 'idle',
      participants: [],
      playback: null,
      quality: null,
      sync: null,
      chat: [],
      typing: [],
      mediaMismatch: false,
      source: null,
      sourceActorId: null,
      countdown: null,
    });
  },

  play: () => connection?.send({ type: 'intent', intent: { kind: 'play' } }),

  pause: (atPositionMs) =>
    connection?.send({ type: 'intent', intent: { kind: 'pause', atPositionMs } }),

  seek: (toPositionMs) =>
    connection?.send({ type: 'intent', intent: { kind: 'seek', toPositionMs } }),

  setRate: (rate) => connection?.send({ type: 'intent', intent: { kind: 'rate', rate } }),

  sendChat: (body, atPositionMs) => {
    const trimmed = body.trim();
    if (trimmed.length === 0) return;

    const localId = crypto.randomUUID();

    // Rendered immediately, then replaced when the relay confirms it. Waiting
    // for the round trip makes a chat feel broken on any real connection.
    set((state) => ({
      chat: [
        ...state.chat,
        {
          id: localId,
          authorId: state.participantId,
          body: trimmed,
          atPositionMs,
          serverTimeMs: Date.now(),
          pending: true,
        },
      ].slice(-300),
    }));

    connection?.send({ type: 'chat', localId, body: trimmed, atPositionMs });
  },

  setTyping: (active) => connection?.send({ type: 'typing', active }),
}));
